import MailIcon from '../../images/mail-icon.inline.svg'
import React, { useCallback, useState } from 'react'
import LoaderButton from '../Buttons/LoaderButton'
import useAxios from '../../hooks/useAxios'
import { alternativeEmailRegex } from '../../utils/validation'
import Grid from '@material-ui/core/Grid/Grid'
import Box from '@material-ui/core/Box/Box'
import Typography from '@material-ui/core/Typography/Typography'
import TextField from '@material-ui/core/TextField/TextField'
import makeStyles from '@material-ui/core/styles/makeStyles'
import createStyles from '@material-ui/core/styles/createStyles'
import { useTranslation } from 'react-i18next'
import { callWithRetry } from '../../utils/utils'
import useUser from '../../hooks/useUser'

type StatusCodes = 'idle' | 'submitting' | 'success' | 'error' | '409'

const NewsLetterForm: React.FC = () => {
  const { t } = useTranslation()
  const classes = useNewsletterFormStyles()
  const [status, setStatus] = useState<StatusCodes>('idle')
  const [helperText, setHelperText] = useState<string>('')
  const axios = useAxios({ withPartnerAuth: true })
  const { refetchSession } = useUser()
  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      const data = new FormData(e.currentTarget)
      const email = data.get('email')?.toString()
      if (!email || !alternativeEmailRegex.test(email)) {
        setStatus('error')
        return setHelperText(t('newsletterForm.emailValidationError'))
      }
      setStatus('submitting')
      return axios
        .post(
          '/api/v2/leads/email',
          { email },
          {
            headers: {
              'Content-Type': 'application/json',
            },
            validateStatus: (status) => [200, 201, 409].includes(status),
          }
        )
        .then(async ({ status }) => {
          setStatus(status === 409 ? `${status}` : 'success')
          await callWithRetry({
            fn: refetchSession,
            retries: 3,
            predicate: async (response) => {
              const { data } = await response
              return !!data?.lead_auth_token
            },
          })
        })
        .catch((e) => {
          setHelperText(e.message)
          setStatus('error')
        })
    },
    [axios, refetchSession, t]
  )

  const handleTextChange = useCallback(() => {
    if (status !== 'idle') setStatus('idle')
  }, [status])

  return (
    <Grid
      container
      spacing={2}
      alignItems="center"
      justifyContent="space-between"
      className={classes.grid}
    >
      <Grid item xs={12} sm={6} className={classes.gridDescription}>
        <Box>
          <MailIcon />
        </Box>
        <Typography color="inherit" className={classes.customFont}>
          {t('newsletterForm.infoText1')}&nbsp;
          <strong>{t('newsletterForm.boldText')}</strong>&nbsp;
          {t('newsletterForm.infoText2')}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={5}>
        {status === '409' && (
          <Typography
            variant="caption"
            color="inherit"
            className={classes.successMessage}
          >
            {t('newsletterForm.successText')}
          </Typography>
        )}
        {status === 'success' && (
          <Typography
            variant="caption"
            color="inherit"
            className={classes.successMessage}
          >
            {t('newsletterForm.successMessage')}
          </Typography>
        )}
        {!['success', '409'].includes(status) && (
          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              onChange={handleTextChange}
              name="email"
              placeholder={t('newsletterForm.emailPlaceholder')}
              fullWidth
              variant="standard"
              type="email"
              required
              InputProps={{
                className: classes.textField,
                disableUnderline: true,
                endAdornment: (
                  <LoaderButton
                    className={classes.subscribeButton}
                    type="submit"
                    loading={status === 'submitting'}
                    disabled={status === 'error'}
                  >
                    {t('newsletterForm.subscribe')}
                  </LoaderButton>
                ),
              }}
              inputProps={{ className: classes.nativeInput }}
              error={status === 'error'}
              helperText={status === 'error' && helperText}
            />
          </Box>
        )}
      </Grid>
    </Grid>
  )
}

const useNewsletterFormStyles = makeStyles((theme) => {
  const whiteColor = theme.colors.white['100']
  const sixteenPx = theme.spacing(2)
  const fourPx = theme.spacing(0.5)
  const twoPx = theme.spacing(0.25)
  return createStyles({
    grid: {
      marginBlock: theme.spacing(3),
    },
    gridDescription: {
      color: whiteColor,
      display: 'flex',
      alignItems: 'center',
      gap: sixteenPx,
    },
    successMessage: {
      lineHeight: 3,
      fontWeight: 'bold',
      background: theme.colors.green['400'],
      color: whiteColor,
      borderRadius: fourPx,
      paddingInline: fourPx,
      paddingBlock: twoPx,
    },
    textField: {
      borderRadius: fourPx,
      background: whiteColor,
    },
    subscribeButton: {
      '&&': {
        border: 'none',
        borderRadius: `0 ${fourPx}px ${fourPx}px 0`,
        height: '45px',
        margin: 0,
        outlineOffset: 0,
      },
    },
    nativeInput: { paddingLeft: sixteenPx, border: 'none' },
    customFont: {
      fontSize: '1.125em',
      textAlign: 'justify',
    },
  })
})

export default NewsLetterForm
